<template>
  <div
    v-if="shouldShowBanner"
    data-testid="maintenance-banner"
    class="fixed left-1/2 top-0 z-[9999] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-bl-lg rounded-br-lg bg-error p-2 text-center text-sm font-medium shadow-sm"
  >
    <div class="flex items-center justify-center gap-2 text-on-error">
      <SdfIcon icon="alert-warning" role="presentation" class="flex-shrink-0 text-[16px]" />
      <div class="min-w-0 flex-1 overflow-x-auto whitespace-nowrap">
        {{ maintenanceBanner?.message }}
      </div>
      <button
        class="ml-2 size-4"
        :aria-label="$t('app.maintenance_banner_dismiss')"
        :title="$t('app.maintenance_banner_dismiss')"
        @click="dismissBanner"
      >
        <SdfIcon icon="action-close" role="presentation" class="text-sm" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SdfIcon } from '@pequity/shared/ui';
import { isRolloutFlagEnabled } from '@pequity/shared/utils/rollout-flags';
import { useSessionStorage } from '@vueuse/core';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { MAINTENANCE_BANNER_FLAG } from '@/components/maintenance-banner/constants';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { IS_PUBLIC, NO_AUTH_NEEDED } from '@/types';

defineOptions({
  name: 'MaintenanceBanner',
});

// Data
const authStore = useAuthStore();
const companyStore = useCompanyStore();
const route = useRoute();
const isDismissed = useSessionStorage('maintenance-banner-dismissed', false);

// Computed
const maintenanceBanner = computed(() => companyStore.company?.maintenance_banner);

// Rollout flags aren't reactive; reading the route first re-evaluates this after each
// navigation, by which point the router guards have loaded the flags.
const shouldShowBanner = computed(
  () =>
    !route.meta[IS_PUBLIC] &&
    !route.meta[NO_AUTH_NEEDED] &&
    authStore.loggedIn &&
    isRolloutFlagEnabled(MAINTENANCE_BANNER_FLAG) &&
    !!maintenanceBanner.value?.message &&
    !isDismissed.value &&
    new Date() < new Date(maintenanceBanner.value.ends_at)
);

// Methods
const dismissBanner = () => {
  isDismissed.value = true;
};
</script>
